import React from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { statusMeta } from '../../utils/propulsionAlarmUtils';

const Block = styled.div`
  margin-bottom: 1.25rem;
  padding: 1rem;
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.border.primary};
  background: ${theme.colors.background.secondary};
`;

const Banner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
  padding: 0.65rem 0.85rem;
  border-radius: ${theme.borderRadius.md};
  background: ${(p) => p.$bg};
  border: 1px solid ${(p) => p.$color}44;
  margin-bottom: 0.85rem;
`;

const BannerTitle = styled.div`
  font-weight: ${theme.fontWeights.semibold};
  color: ${(p) => p.$color};
  font-size: ${theme.fontSizes.sm};
`;

const BannerSub = styled.div`
  font-size: ${theme.fontSizes.xs};
  color: ${theme.colors.text.secondary};
`;

const SectionLabel = styled.div`
  font-size: 0.68rem;
  font-weight: ${theme.fontWeights.semibold};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${theme.colors.text.secondary};
  margin-bottom: 0.5rem;
`;

const IssueList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const IssueItem = styled.li`
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.45rem 0;
  border-bottom: 1px solid ${theme.colors.border.secondary};
  font-size: ${theme.fontSizes.sm};

  &:last-child {
    border-bottom: none;
  }
`;

const IssueName = styled.span`
  color: ${theme.colors.text.primary};
  font-weight: ${theme.fontWeights.medium};
`;

const IssueState = styled.span`
  color: ${(p) => p.$color};
  font-weight: ${theme.fontWeights.semibold};
  text-transform: uppercase;
  font-size: ${theme.fontSizes.xs};
`;

const Meta = styled.div`
  margin-top: 0.75rem;
  font-size: ${theme.fontSizes.xs};
  color: ${theme.colors.text.secondary};
  line-height: 1.45;
`;

function EngineSide({ title, issues }) {
  if (!issues?.length) return null;
  return (
    <div style={{ marginBottom: '0.75rem' }}>
      <SectionLabel>{title}</SectionLabel>
      <IssueList>
        {issues.map((issue) => (
          <IssueItem key={issue.path}>
            <IssueName>{issue.label}</IssueName>
            <IssueState $color={issue.state === 'alarm' ? '#ef4444' : '#f59e0b'}>
              {issue.rawState || issue.state}
            </IssueState>
          </IssueItem>
        ))}
      </IssueList>
    </div>
  );
}

function formatLastUpdate(date) {
  if (!date) return 'unknown';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const EngineAlarmPanel = ({ rollup }) => {
  if (!rollup || rollup.pathCount === 0) return null;

  const meta = statusMeta(rollup.status);
  const bannerText =
    rollup.status === 'clear'
      ? `${rollup.normalCount} checks normal`
      : rollup.status === 'idle'
        ? `${rollup.normalCount} checks — last update while engines were off`
        : rollup.status === 'stale'
          ? 'Alarm data is old but engines appear to be running'
          : rollup.status === 'no_data'
            ? 'No engine alarm paths in stream yet'
            : `${rollup.issueCount} active condition${rollup.issueCount === 1 ? '' : 's'}`;

  return (
    <Block>
      <SectionLabel>Engine alarms (N2K)</SectionLabel>
      <Banner $bg={meta.bg} $color={meta.color}>
        <BannerTitle $color={meta.color}>{meta.label}</BannerTitle>
        <BannerSub>{bannerText}</BannerSub>
      </Banner>

      <EngineSide title="Port engine" issues={rollup.port} />
      <EngineSide title="Starboard engine" issues={rollup.starboard} />

      {rollup.status === 'clear' || rollup.status === 'idle' ? (
        <Meta>
          Port and starboard alarm flags are NORMAL. Check engine, oil pressure, temperature, and
          related N2K alerts are monitored automatically.
        </Meta>
      ) : null}

      <Meta>
        Last alarm update: {formatLastUpdate(rollup.lastUpdate)}
        {rollup.enginesRunning ? ' · Engines running' : ' · Engines off / idle'}
        {rollup.pathCount ? ` · ${rollup.pathCount} paths monitored` : ''}
      </Meta>
    </Block>
  );
};

export default EngineAlarmPanel;
